import { useEffect } from 'react'
import { useTheme, BrandTheme } from '@/contexts/theme-context'
import { brandName } from '@/lib/runtime-config'
import { usePageTitleContext } from '@/contexts/page-title-context'

/* eslint-disable i18next/no-literal-string -- brand names are not translatable */
function getAppName(brand: BrandTheme): string {
  switch (brand) {
    case BrandTheme.LUMON: return 'Lumon Industries'
    case BrandTheme.SWISS: return 'Swiss Admin'
    case BrandTheme.MUENCHENSTIFT: return 'MÜNCHENSTIFT'
    case BrandTheme.BUNDESDRUCKEREI: return 'Bundesdruckerei'
    case BrandTheme.AARGAU: return 'Kanton Aargau'
    case BrandTheme.DEFAULT: return 'ID Pulse'
    case BrandTheme.TENANT: return brandName() ?? 'ID Pulse'
  }
}
/* eslint-enable i18next/no-literal-string */

/**
 * Hook to set the document title for a page.
 * Automatically appends the brand-specific app name suffix.
 *
 * @param title - The page-specific title (e.g., "Identities", "Dashboard")
 *
 * @example
 * // Sets document.title to "Identities | iam MÜNCHENSTIFT"
 * usePageTitle('Identities')
 */
export function usePageTitle(title?: string | null) {
  const { brandTheme } = useTheme()
  const appName = getAppName(brandTheme)
  const { setTitle } = usePageTitleContext()

  useEffect(() => {
    /* WHY: An Outlet parent (e.g. AccessGovernance) and its child route both
       call this hook. Child effects run before parent effects on mount, so an
       unconditional parent setTitle would override the child. Callers signal
       "I don't own the title here" by passing null/undefined; we no-op so the
       child's title remains. The cleanup is symmetric — we only reset what
       this instance set. */
    if (!title) return
    document.title = `${title} | ${appName}`
    setTitle(title)
    return () => {
      document.title = appName
      setTitle('')
    }
  }, [title, appName, setTitle])
}

/**
 * High-priority variant — used by {@link PageLayout}'s UniversalHeader. The
 * H1 context resolves the high slot first, so PageLayout's entity-specific
 * title (e.g. "Security Team Approval") wins over any page-level
 * `usePageTitle('Approval Policy Details | …')` call regardless of which
 * useEffect runs first. Document.title still uses the same suffix scheme.
 */
export function usePageLayoutTitle(title?: string | null) {
  const { brandTheme } = useTheme()
  const appName = getAppName(brandTheme)
  const { setHighPriorityTitle } = usePageTitleContext()

  useEffect(() => {
    if (!title) return
    document.title = `${title} | ${appName}`
    setHighPriorityTitle(title)
    return () => {
      document.title = appName
      setHighPriorityTitle('')
    }
  }, [title, appName, setHighPriorityTitle])
}
